// src/oura/components/docs/write/QuestionsView.tsx
//
// The Questions editor (B15), a view inside the doc drawer. Staff write each
// question in Oura: its code, its kind (agree, change, choice, text, file)
// and, for a choice, the options. The preview shows the doc exactly as it
// will read: the numbered "Your questions" list in the body and 1 comment
// per new question ("[S2-MK] Q3 ... Reply AGREED, or tell us what to
// change."). "Send to the doc" posts both; then the 3 counts that must agree
// show: in Oura, in the doc's list, and its comments.
//
// A question the client has answered stays as it is: no edit, no remove.
// Removing a sent question deletes its comment in the doc, so it asks first.

import { useState, type ReactNode } from "react";
import { CheckIcon, PlusIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import {
    addQuestion,
    deleteQuestion,
    sendQuestions,
    updateQuestion,
    type DocQuestion,
    type QuestionKind,
} from "@service/aeo/clientDocsWrite";
import type { DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { QUESTION_LIST_HEADING, questionCommentOf, questionListLineOf } from "@service/aeo/clientDocsRules";
import { cn } from "../../../lib/cn";
import {
    DrawerBlock,
    DrawerFoot,
    DrawerPulse,
    FOCUS_RING,
    FamilyChip,
    InnerStat,
    PillButton,
    SURFACE,
    Seg,
    TYPE,
    Tag,
    type DrawerConfirm,
    type DrawerFootMessage,
} from "@oura/ui";
import { codeFamily, docCodeChips, docsFailureText, ouraAccountOnDoc, plural } from "@oura/docs";
import { inputClass, textareaClass } from "../../../onboarding/messageText";
import type { DocWriteView } from "./model";

const KIND_LABEL: Record<QuestionKind, string> = {
    agree: "Yes / No",
    change: "Change",
    choice: "Choice",
    text: "Text",
    file: "File",
};

/** What each kind asks the client for, under the kind picker. */
const KIND_HINT: Record<QuestionKind, string> = {
    agree: "They say yes, or what to change.",
    change: "They say what to change.",
    choice: "They pick 1 of your options.",
    text: "They write an answer.",
    file: "They send a file.",
};

const KINDS = Object.keys(KIND_LABEL) as QuestionKind[];
const ALL_CODES = "";

interface Draft {
    code: string;
    kind: QuestionKind;
    text: string;
    options: string[];
}

const blankDraft = (): Draft => ({ code: ALL_CODES, kind: "agree", text: "", options: ["", ""] });
const draftOf = (q: DocQuestion): Draft => ({ code: q.code ?? ALL_CODES, kind: q.kind, text: q.text, options: q.kind === "choice" ? [...q.options] : ["", ""] });

/** Whether the client has said anything on this question's thread. */
function answered(q: DocQuestion, detail: DocumentDetail): boolean {
    const t = q.threadId ? detail.threads.find((x) => x.id === q.threadId) : null;
    return Boolean(t && (t.resolved || t.posts.some((p) => p.author.side === "client")));
}

/** The form for a new question or an edit. */
function QuestionForm({
    doc,
    initial,
    busy,
    error,
    onSave,
    onCancel,
}: {
    doc: StaffDocument;
    initial: Draft;
    busy: boolean;
    error: string | null;
    onSave: (d: Draft) => void;
    onCancel: () => void;
}) {
    const [d, setD] = useState<Draft>(initial);
    const [tried, setTried] = useState(false);
    const chips = docCodeChips(doc);
    const options = d.options.map((o) => o.trim()).filter(Boolean);
    const textError = tried && !d.text.trim() ? "Write the question first." : null;
    const optionsError = tried && d.kind === "choice" && options.length < 2 ? "Give at least 2 options." : null;
    const save = () => {
        setTried(true);
        if (!d.text.trim() || (d.kind === "choice" && options.length < 2)) return;
        onSave(d);
    };
    const setOption = (i: number, v: string) => setD({ ...d, options: d.options.map((o, j) => (j === i ? v : o)) });

    return (
        <div className={cn(SURFACE.inner, "space-y-3 py-3")}>
            {chips.length > 1 && (
                <label className="block">
                    <span className={cn(TYPE.lab, "mb-1.5 block")}>Code</span>
                    <select className={inputClass()} value={d.code} onChange={(e) => setD({ ...d, code: e.target.value })} disabled={busy}>
                        <option value={ALL_CODES}>Every code in the doc</option>
                        {chips.map((c) => (
                            <option key={c.label} value={c.label}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                </label>
            )}
            <div>
                <p className={cn(TYPE.lab, "m-0 mb-1.5")}>Kind</p>
                <Seg<QuestionKind>
                    size="sm"
                    ariaLabel="Kind of question"
                    value={d.kind}
                    onChange={(kind) => setD({ ...d, kind })}
                    items={KINDS.map((k) => ({ key: k, label: KIND_LABEL[k] }))}
                />
                <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>{KIND_HINT[d.kind]}</p>
            </div>
            <label className="block">
                <span className={cn(TYPE.lab, "mb-1.5 block")}>Question</span>
                <textarea
                    className={textareaClass(textError)}
                    rows={3}
                    value={d.text}
                    onChange={(e) => setD({ ...d, text: e.target.value })}
                    disabled={busy}
                    aria-invalid={Boolean(textError)}
                />
                {textError && <span role="alert" className={cn(TYPE.tc, "mt-1.5 block font-medium text-rose-600")}>{textError}</span>}
            </label>
            {d.kind === "choice" && (
                <div>
                    <p className={cn(TYPE.lab, "m-0 mb-1.5")}>Options</p>
                    <div className="space-y-2">
                        {d.options.map((o, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <input
                                    className={inputClass(optionsError && !o.trim() ? optionsError : null)}
                                    value={o}
                                    aria-label={`Option ${i + 1}`}
                                    onChange={(e) => setOption(i, e.target.value)}
                                    disabled={busy}
                                />
                                {d.options.length > 2 && (
                                    <button
                                        type="button"
                                        aria-label={`Remove option ${i + 1}`}
                                        onClick={() => setD({ ...d, options: d.options.filter((_, j) => j !== i) })}
                                        className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-full text-gray-400 hover:text-gray-700", FOCUS_RING)}
                                    >
                                        <XIcon aria-hidden size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {optionsError && <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>{optionsError}</p>}
                    <PillButton variant="text" icon={PlusIcon} className="mt-2" onClick={() => setD({ ...d, options: [...d.options, ""] })} disabled={busy}>
                        Add an option
                    </PillButton>
                </div>
            )}
            {error && <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>{error}</p>}
            <div className="flex flex-wrap items-center gap-2">
                <PillButton variant="primary" busy={busy} onClick={save}>
                    Save
                </PillButton>
                <PillButton disabled={busy} onClick={onCancel}>
                    Cancel
                </PillButton>
            </div>
        </div>
    );
}

/** One question in Oura's list: "Q3" or "New", its text on 1 line, code and kind. */
function QuestionRow({ q, n, locked, onEdit, onRemove }: { q: DocQuestion; n: number; locked: boolean; onEdit: () => void; onRemove: () => void }) {
    return (
        <div className={cn("flex items-center gap-2 border-t py-2 first:border-t-0", SURFACE.hairline)}>
            <span className={cn(TYPE.tc, "w-9 shrink-0 font-semibold", q.state === "draft" ? "text-blue-600" : "text-gray-500")}>
                {q.state === "draft" ? "New" : `Q${q.number ?? n}`}
            </span>
            <span className={cn(TYPE.tb, "min-w-0 flex-1 truncate text-gray-900")} title={q.text}>
                {q.text}
            </span>
            {q.code && <FamilyChip family={codeFamily(q.code)}>{q.code}</FamilyChip>}
            <Tag tone="gray" className="max-sm:hidden">
                {KIND_LABEL[q.kind]}
            </Tag>
            {locked ? (
                <span className={cn(TYPE.tc, "shrink-0 text-gray-500")}>Answered</span>
            ) : (
                <span className="flex shrink-0 items-center gap-3">
                    <PillButton variant="text" onClick={onEdit} aria-label={`Edit: ${q.text}`}>
                        Edit
                    </PillButton>
                    <PillButton variant="text" onClick={onRemove} aria-label={`Remove: ${q.text}`}>
                        Remove
                    </PillButton>
                </span>
            )}
        </div>
    );
}

/** The doc as it will read: the numbered list, and the comments still to post. */
function Preview({ sent, drafts, firstNew, ouraAccount }: { sent: DocQuestion[]; drafts: DocQuestion[]; firstNew: number; ouraAccount: string | null }) {
    if (sent.length === 0 && drafts.length === 0) return null;
    return (
        <DrawerBlock label="How it reads in the doc">
            <div className={cn(SURFACE.inner, "mt-2")}>
                <p className={cn(TYPE.tc, "m-0 font-semibold text-gray-900")}>{QUESTION_LIST_HEADING}</p>
                <ol className="m-0 mt-1 list-plain space-y-1 p-0">
                    {sent.map((q) => (
                        <li key={q.id} className={cn(TYPE.tc, "break-words text-gray-700")}>
                            {questionListLineOf(q, q.number ?? 0)}
                        </li>
                    ))}
                    {drafts.map((q, i) => (
                        <li key={q.id} className={cn(TYPE.tc, "break-words font-medium text-blue-700")}>
                            {questionListLineOf(q, firstNew + i)}
                        </li>
                    ))}
                </ol>
            </div>
            {drafts.length > 0 && (
                <>
                    <p className={cn(TYPE.lab, "m-0 mt-3")}>{plural(drafts.length, "new comment")}</p>
                    <ul className="m-0 mt-1 list-plain space-y-2 p-0">
                        {drafts.map((q, i) => (
                            <li key={q.id} className={cn(SURFACE.inner, TYPE.tc, "break-words text-gray-900")}>
                                {/* The account from the doc's own share list: it differs per environment (#3974). */}
                                <span className="text-gray-500">{ouraAccount ? `ourteam (${ouraAccount})` : "ourteam"}</span>
                                <span className="mt-0.5 block">{questionCommentOf(q, firstNew + i)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </DrawerBlock>
    );
}

/** In Oura, in the doc's list, and its comments: the 3 counts that must agree once sent. */
function CountCheck({ detail }: { detail: DocumentDetail }) {
    const c = detail.questionCheck;
    if (!c) return null;
    const show = (n: number | null) => (n === null ? "Not read yet" : String(n));
    const agree = (c.inList === null || c.inList === c.inOura) && (c.comments === null || c.comments === c.inOura);
    return (
        <DrawerBlock label="Counts in Oura and the doc">
            <div className="mt-2 grid grid-cols-3 gap-2">
                <InnerStat label="In Oura" value={<span className="tabular-nums">{c.inOura}</span>} />
                <InnerStat label="In the list" value={<span className="tabular-nums">{show(c.inList)}</span>} />
                <InnerStat label="Comments" value={<span className="tabular-nums">{show(c.comments)}</span>} />
            </div>
            <p className={cn(TYPE.tc, "m-0 mt-2 flex items-start gap-1.5", agree ? "text-gray-700" : "font-medium text-amber-800")}>
                {agree ? (
                    <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                ) : (
                    <TriangleAlertIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-amber-600" />
                )}
                {agree ? "They match." : "They differ. Open the doc and check its list and comments against Oura's."}
            </p>
        </DrawerBlock>
    );
}

export function QuestionsView({
    doc,
    detail,
    loading,
    onBusy,
    onView,
}: {
    doc: StaffDocument;
    detail: DocumentDetail | null;
    loading: boolean;
    onBusy: (busy: boolean) => void;
    onView: (view: DocWriteView | "doc") => void;
}) {
    /** Which form is open: a question's id, "new", or none. */
    const [editing, setEditing] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [removing, setRemoving] = useState<DocQuestion | null>(null);
    const [sentNow, setSentNow] = useState(false);

    const run = async <T,>(work: () => Promise<T>): Promise<T> => {
        setBusy(true);
        onBusy(true);
        try {
            return await work();
        } finally {
            setBusy(false);
            onBusy(false);
        }
    };

    if (!detail) {
        return (
            <DrawerBlock>
                {loading ? <DrawerPulse lines={3} label="Loading the questions" /> : <p className="m-0 text-gray-700">Oura could not read this doc&rsquo;s questions.</p>}
            </DrawerBlock>
        );
    }

    const list = detail.questionList ?? [];
    const drafts = list.filter((q) => q.state === "draft");
    const sent = list.filter((q) => q.state === "sent").sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    // New questions follow every question already in the doc, Oura's or written by hand, as the drawer numbers them.
    const asked = detail.threads.filter((t) => t.kind === "question").length;
    const firstNew = Math.max(asked, sent.reduce((max, q) => Math.max(max, q.number ?? 0), 0)) + 1;

    const save = (d: Draft, q: DocQuestion | null) =>
        void run(async () => {
            setFormError(null);
            const body = { code: d.code || null, kind: d.kind, text: d.text.trim(), options: d.kind === "choice" ? d.options.map((o) => o.trim()).filter(Boolean) : [] };
            const r = q ? await updateQuestion(doc.id, q.id, body) : await addQuestion(doc.id, body);
            if (!r.ok) {
                setFormError(docsFailureText(r, "the question"));
                return;
            }
            setEditing(null);
            setSentNow(false);
            setMessage({ tone: "success", text: q ? (q.state === "sent" ? `Q${q.number} changed in the doc too.` : "Question saved.") : "Question added. Send it to the doc when the list is ready." });
        });

    const remove = (q: DocQuestion) =>
        void run(async () => {
            const r = await deleteQuestion(doc.id, q.id);
            setRemoving(null);
            setMessage(r.ok ? { tone: "success", text: q.state === "sent" ? `Q${q.number} removed from the doc.` : "Draft removed." } : { tone: "error", text: docsFailureText(r, "the question") });
        });

    const send = () =>
        void run(async () => {
            const n = drafts.length;
            const r = await sendQuestions(doc.id);
            if (!r.ok) {
                setMessage({ tone: "error", text: docsFailureText(r, "the questions") });
                return;
            }
            const c = r.data.questionCheck;
            setSentNow(true);
            setMessage({
                tone: "success",
                text: c ? `Sent ${plural(n, "question")}. The doc's list and its comments now show ${c.inOura}.` : `Sent ${plural(n, "question")} to the doc.`,
            });
        });

    const confirm: DrawerConfirm | null = removing
        ? {
            text: removing.state === "sent" ? `Remove Q${removing.number}? Its comment in the doc is deleted too.` : "Remove this draft?",
            verb: "Remove",
            busy,
            onConfirm: () => remove(removing),
            onCancel: () => setRemoving(null),
        }
        : null;

    // After sending, the next step is offered: share it, or tell the client.
    const shared = doc.sharing.clientCanSee;
    const nextStep: ReactNode = sentNow ? (
        shared ? (
            <PillButton variant="primary" onClick={() => onView("review")}>
                Write the review email
            </PillButton>
        ) : (
            <PillButton variant="primary" onClick={() => onView("share")}>
                Share with the client
            </PillButton>
        )
    ) : null;
    const first = nextStep ?? (drafts.length > 0 ? (
        <PillButton variant="primary" busy={busy && editing === null && !removing} disabled={busy || editing !== null} onClick={send}>
            {`Send ${plural(drafts.length, "question")} to the doc`}
        </PillButton>
    ) : undefined);
    const second = (
        <PillButton disabled={busy} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );

    return (
        <>
            <DrawerBlock label={`In Oura · ${list.length}`} busy={busy}>
                {list.length === 0 && editing !== "new" && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>No questions written in Oura for this doc yet.</p>}
                <div className="mt-1">
                    {[...sent, ...drafts].map((q, i) =>
                        editing === q.id ? (
                            <div key={q.id} className="py-2">
                                <QuestionForm doc={doc} initial={draftOf(q)} busy={busy} error={formError} onSave={(d) => save(d, q)} onCancel={() => setEditing(null)} />
                            </div>
                        ) : (
                            <QuestionRow
                                key={q.id}
                                q={q}
                                n={i + 1}
                                locked={q.state === "sent" && answered(q, detail)}
                                onEdit={() => {
                                    setFormError(null);
                                    setEditing(q.id);
                                }}
                                onRemove={() => (q.state === "draft" ? remove(q) : setRemoving(q))}
                            />
                        ),
                    )}
                </div>
                {editing === "new" ? (
                    <div className="mt-2">
                        <QuestionForm doc={doc} initial={blankDraft()} busy={busy} error={formError} onSave={(d) => save(d, null)} onCancel={() => setEditing(null)} />
                    </div>
                ) : (
                    <PillButton
                        variant="text"
                        icon={PlusIcon}
                        className="mt-2"
                        disabled={busy || editing !== null}
                        onClick={() => {
                            setFormError(null);
                            setEditing("new");
                        }}
                    >
                        Add a question
                    </PillButton>
                )}
            </DrawerBlock>

            <Preview sent={sent} drafts={drafts} firstNew={firstNew} ouraAccount={ouraAccountOnDoc(doc)} />
            <CountCheck detail={detail} />

            <DrawerFoot message={message} confirm={confirm} first={first} second={second} />
        </>
    );
}
